import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { roleDefaultRate } from "@/lib/calc";
import { NumCell } from "@/components/estimation/cells";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LOC_CLS = {
  onshore: "bg-blue-50 text-[#002FA7] border-[#002FA7]/30",
  offshore: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export default function RateCardTab({ est, setEst, oppId }) {
  const [newRole, setNewRole] = useState("");
  const [syncing, setSyncing] = useState(false);

  const saveRoles = (roles) => {
    setEst({ ...est, roles });
    api.put(`/opportunities/${oppId}/estimation/roles`, { roles })
      .then((r) => setEst(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const patchRole = (idx, patch) => {
    const roles = est.roles.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    saveRoles(roles);
  };

  const saveSettings = (patch) => {
    setEst({ ...est, ...patch });
    api.put(`/opportunities/${oppId}/estimation/settings`, patch)
      .then((r) => setEst(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const addRole = () => {
    const name = newRole.trim();
    if (!name) return;
    if (est.roles.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Role already exists");
      return;
    }
    saveRoles([...est.roles, { name, onsite_rate: 100, offshore_rate: 45, default_location: "offshore" }]);
    setNewRole("");
  };

  const removeRole = (name) => {
    if (!window.confirm(`Remove role "${name}"? Its % allocations on tasks will be cleared.`)) return;
    saveRoles(est.roles.filter((r) => r.name !== name));
  };

  const syncFromMaster = async () => {
    if (!window.confirm(
      "Overwrite this opportunity's onsite/offshore rates and default delivery locations with the current Master Rate Card?\n\nNew roles from the master will be added. Roles unique to this opportunity will be kept.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/sync-rates`);
      setEst(data);
      toast.success("Rates synced from Master Rate Card");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const th = "bg-zinc-50 border-b border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium";

  return (
    <div data-testid="ratecard-tab" className="grid grid-cols-1 xl:grid-cols-3 gap-8">
      <div className="xl:col-span-2 bg-white border border-zinc-200 rounded-sm">
        <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-zinc-200">
          <div>
            <h3 className="font-heading text-sm font-medium tracking-tight">Resource billing — onshore / offshore</h3>
            <p className="text-xs text-zinc-400 mt-0.5">Rates default to the role&apos;s preferred location; override per row on the Resource Load tab.</p>
          </div>
          <button
            data-testid="sync-from-master-btn"
            onClick={syncFromMaster}
            disabled={syncing}
            className="flex items-center gap-1.5 text-xs border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] rounded-sm px-3 py-1.5 disabled:opacity-50">
            <RefreshCw size={12} strokeWidth={1.5} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync from master"}
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr>
              <th className={`${th} text-left`}>Role</th>
              <th className={`${th} text-right`}>Onsite $/hr</th>
              <th className={`${th} text-right`}>Offshore $/hr</th>
              <th className={`${th} text-center`}>Default location</th>
              <th className={`${th} text-right`}>Rate $/hr</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {est.roles.map((r, i) => {
              const loc = (r.default_location || "offshore").toLowerCase();
              return (
                <tr key={r.name} className="hover:bg-zinc-50">
                  <td className="border-b border-zinc-100 px-3 py-1.5 text-[13px]">{r.name}</td>
                  <td className="border-b border-zinc-100 w-24"><NumCell value={r.onsite_rate} onCommit={(v) => patchRole(i, { onsite_rate: v || 0 })} testId={`rate-onsite-${i}`} /></td>
                  <td className="border-b border-zinc-100 w-24"><NumCell value={r.offshore_rate} onCommit={(v) => patchRole(i, { offshore_rate: v || 0 })} testId={`rate-offshore-${i}`} /></td>
                  <td className="border-b border-zinc-100 px-2 text-center w-32">
                    <select
                      data-testid={`rate-default-loc-${i}`}
                      value={loc}
                      onChange={(e) => patchRole(i, { default_location: e.target.value })}
                      className={`text-[11px] font-medium uppercase tracking-wide rounded-sm border px-2 py-0.5 outline-none focus:ring-1 focus:ring-[#002FA7] ${LOC_CLS[loc]}`}>
                      <option value="onshore">Onshore</option>
                      <option value="offshore">Offshore</option>
                    </select>
                  </td>
                  <td className="border-b border-zinc-100 px-3 text-right font-mono text-[13px] font-medium text-[#002FA7]" data-testid={`rate-default-${i}`}>${roleDefaultRate(r).toFixed(1)}</td>
                  <td className="border-b border-zinc-100 px-2 text-center w-10">
                    <button data-testid={`remove-role-${i}`} onClick={() => removeRole(r.name)} className="text-zinc-300 hover:text-red-600 p-1"><Trash2 size={12} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center gap-2 px-4 py-3 border-t border-zinc-100">
          <input data-testid="new-role-input" value={newRole} onChange={(e) => setNewRole(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addRole()} placeholder="Add resource / skill (e.g. DevOps Engineer)…"
            className="border border-zinc-300 rounded-sm px-3 py-1.5 text-xs w-72 outline-none focus:border-[#002FA7]" />
          <button data-testid="add-role-button" onClick={addRole}
            className="flex items-center gap-1 text-xs text-white bg-[#002FA7] hover:bg-[#002280] rounded-sm px-3 py-1.5">
            <Plus size={12} /> Add role
          </button>
        </div>
      </div>

      <div className="space-y-8">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">T-shirt sizing</h3></div>
          <table className="w-full">
            <thead><tr><th className={`${th} text-left`}>Size</th><th className={`${th} text-right`}>Hours</th></tr></thead>
            <tbody>
              {Object.entries(est.tshirt_sizes).map(([s, h]) => (
                <tr key={s} className="hover:bg-zinc-50">
                  <td className="border-b border-zinc-100 px-3 py-1.5 font-mono text-[13px] font-medium">{s}</td>
                  <td className="border-b border-zinc-100 w-24">
                    <NumCell value={h} testId={`size-hours-${s}`}
                      onCommit={(v) => saveSettings({ tshirt_sizes: { ...est.tshirt_sizes, [s]: v || 0 } })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-4">
          <h3 className="font-heading text-sm font-medium tracking-tight">Planning inputs</h3>
          <div className="flex items-center justify-between gap-4">
            <label className="text-xs text-zinc-600">Hours per FTE-week</label>
            <div className="w-20"><NumCell value={est.hours_per_week} onCommit={(v) => saveSettings({ hours_per_week: v || 40 })} testId="hours-per-week-input" className="border border-zinc-300" /></div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <label className="text-xs text-zinc-600">Load reconciliation tolerance ±%</label>
            <div className="w-20"><NumCell value={est.tolerance_pct} scale={100} onCommit={(v) => saveSettings({ tolerance_pct: v || 0.05 })} testId="tolerance-input" className="border border-zinc-300" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
